
import React from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface EventDetailsFormProps {
  eventTitle: string;
  setEventTitle: (value: string) => void;
  eventType: string;
  setEventType: (value: string) => void;
  eventDescription: string;
  setEventDescription: (value: string) => void;
  duration: string;
  setDuration: (value: string) => void;
  participants: string[];
  participantInput: string;
  setParticipantInput: (value: string) => void;
  handleAddParticipant: () => void;
  handleRemoveParticipant: (participant: string) => void;
}

const EventDetailsForm: React.FC<EventDetailsFormProps> = ({
  eventTitle,
  setEventTitle,
  eventType,
  setEventType,
  eventDescription,
  setEventDescription,
  duration,
  setDuration,
  participants,
  participantInput,
  setParticipantInput,
  handleAddParticipant,
  handleRemoveParticipant
}) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="event-title">Event Title</Label>
        <Input
          id="event-title"
          placeholder="Enter event title"
          value={eventTitle}
          onChange={(e) => setEventTitle(e.target.value)}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="event-type">Event Type</Label>
        <Select value={eventType} onValueChange={setEventType}>
          <SelectTrigger>
            <SelectValue placeholder="Select event type" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="meeting">Meeting</SelectItem>
              <SelectItem value="recording">Recording Session</SelectItem>
              <SelectItem value="rehearsal">Rehearsal</SelectItem>
              <SelectItem value="performance">Performance</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
              <SelectItem value="networking">Networking Event</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="event-description">Description</Label>
        <Textarea
          id="event-description"
          placeholder="Describe the event (optional)"
          value={eventDescription}
          onChange={(e) => setEventDescription(e.target.value)}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="duration">Duration (minutes)</Label>
        <Select value={duration} onValueChange={setDuration}>
          <SelectTrigger>
            <SelectValue placeholder="Select duration" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="15">15 minutes</SelectItem>
              <SelectItem value="30">30 minutes</SelectItem>
              <SelectItem value="45">45 minutes</SelectItem>
              <SelectItem value="60">1 hour</SelectItem>
              <SelectItem value="90">1.5 hours</SelectItem>
              <SelectItem value="120">2 hours</SelectItem>
              <SelectItem value="180">3 hours</SelectItem>
              <SelectItem value="240">4 hours</SelectItem>
              <SelectItem value="480">8 hours</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label>Participants</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Enter email address"
            value={participantInput}
            onChange={(e) => setParticipantInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddParticipant();
              }
            }}
          />
          <Button type="button" onClick={handleAddParticipant} variant="outline">
            Add
          </Button>
        </div>
        
        {participants.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {participants.map(participant => (
              <Badge 
                key={participant} 
                variant="secondary"
                className="flex items-center gap-1"
              >
                {participant}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-3 w-3 ml-1 hover:bg-transparent" 
                  onClick={() => handleRemoveParticipant(participant)}
                >
                  <span className="sr-only">Remove</span>
                  <span className="text-xs">×</span>
                </Button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailsForm;
